import { useEffect, useRef, useCallback, Dispatch } from 'react';
import ReactPlayer from 'react-player';

import { VideoPlayerActionsEnum } from '../utils/enum';
import { VideoPlayerAction } from './useVideoPlayerState';

const SEEK_COOLDOWN_MS = 500;
const { SET_SEEKING, SET_PLAYED_SECONDS } = VideoPlayerActionsEnum;

interface UseVideoSeekProps {
	playerRef: React.RefObject<ReactPlayer>;
	playedSeconds: number;
	dispatch: Dispatch<VideoPlayerAction>;
	seekTo?: number;
	onSeek?: (seconds: number) => void;
	showControls: () => void;
}

export function useVideoSeek({
	playerRef,
	playedSeconds,
	dispatch,
	seekTo,
	onSeek,
	showControls
}: Readonly<UseVideoSeekProps>) {
	const pendingExternalSeekRef = useRef<number | null>(null);
	const lastAppliedExternalSeekRef = useRef<number | null>(null);
	const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const targetSeekPositionRef = useRef<number | null>(null);
	const hasInitializedRef = useRef<boolean>(false);
	const showControlsRef = useRef(showControls);
	const playedSecondsRef = useRef(playedSeconds);
	const onSeekRef = useRef(onSeek);

	onSeekRef.current = onSeek;
	showControlsRef.current = showControls;
	playedSecondsRef.current = playedSeconds;

	const clearSeekTimeout = useCallback((): void => {
		if (seekTimeoutRef.current) {
			clearTimeout(seekTimeoutRef.current);
			seekTimeoutRef.current = null;
		}
	}, []);

	const startSeekCooldown = useCallback((): void => {
		clearSeekTimeout();
		dispatch({ type: SET_SEEKING, payload: true });
		seekTimeoutRef.current = setTimeout(() => {
			dispatch({ type: SET_SEEKING, payload: false });
			targetSeekPositionRef.current = null;
		}, SEEK_COOLDOWN_MS);
	}, [clearSeekTimeout, dispatch]);

	const isAtTargetPosition = useCallback((currentSeconds: number, targetSeconds: number): boolean => {
		return Math.abs(currentSeconds - targetSeconds) < 0.5;
	}, []);

	const applyExternalSeek = useCallback(
		(seconds: number): void => {
			if (Number.isNaN(seconds)) {
				return;
			}

			if (!playerRef.current) {
				pendingExternalSeekRef.current = seconds;
				return;
			}

			if (targetSeekPositionRef.current !== null && isAtTargetPosition(seconds, targetSeekPositionRef.current)) {
				return;
			}

			targetSeekPositionRef.current = seconds;
			startSeekCooldown();
			playerRef.current.seekTo(seconds, 'seconds');
			dispatch({ type: SET_PLAYED_SECONDS, payload: seconds });
			onSeekRef.current?.(seconds);
			pendingExternalSeekRef.current = null;
			lastAppliedExternalSeekRef.current = seconds;
			showControlsRef.current();
		},
		[dispatch, isAtTargetPosition, startSeekCooldown, playerRef]
	);

	const handleSeek = useCallback(
		(seconds: number): void => {
			if (Number.isNaN(seconds)) {
				return;
			}

			if (isAtTargetPosition(seconds, playedSecondsRef.current)) {
				return;
			}

			targetSeekPositionRef.current = seconds;
			startSeekCooldown();
			dispatch({ type: SET_PLAYED_SECONDS, payload: seconds });
			if (playerRef.current) {
				playerRef.current.seekTo(seconds, 'seconds');
			}
			onSeekRef.current?.(seconds);
			showControlsRef.current();
		},
		[dispatch, isAtTargetPosition, startSeekCooldown, playerRef]
	);

	const prepareForExpand = useCallback((seconds: number): void => {
		hasInitializedRef.current = false;
		pendingExternalSeekRef.current = seconds;
	}, []);

	const handleReady = useCallback((): void => {
		if (!playerRef.current) {
			return;
		}

		if (hasInitializedRef.current) {
			return;
		}
		hasInitializedRef.current = true;

		if (pendingExternalSeekRef.current !== null) {
			applyExternalSeek(pendingExternalSeekRef.current);
			return;
		}

		if (playedSecondsRef.current > 0) {
			targetSeekPositionRef.current = playedSecondsRef.current;
			playerRef.current.seekTo(playedSecondsRef.current, 'seconds');
		}
	}, [applyExternalSeek, playerRef]);

	useEffect(() => {
		if (seekTo === undefined) {
			return;
		}

		if (lastAppliedExternalSeekRef.current !== null && Math.abs(lastAppliedExternalSeekRef.current - seekTo) < 0.01) {
			return;
		}

		applyExternalSeek(seekTo);
	}, [seekTo, applyExternalSeek]);

	useEffect(() => {
		return () => {
			clearSeekTimeout();
		};
	}, [clearSeekTimeout]);

	return {
		handleSeek,
		handleReady,
		prepareForExpand
	};
}
